import { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import Footer from '../components/Footer'
import api, { handleAxiosError } from '../config/api'
import { useTranslation } from 'react-i18next'
import {
  applyServerRateLimit,
  AUTH_RATE_LIMIT,
  consumeRateLimit,
} from '../utils/clientRateLimit'
import {
  trackOtpEntered,
  trackOtpResend,
  trackOtpTimeout,
  trackSignupComplete,
} from '../utils/ga4Events'
import { trackOtpConversion } from '../utils/marketingPixels'
import { getCookie } from '../utils/profileRedirect'

const getAnalyticsLanguageLabel = (languageCode) =>
  languageCode === 'ta' ? 'Tamil' : 'English'

function OTPVerification() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const mobile = localStorage.getItem('mobile')

  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [otpError, setOtpError] = useState('')
  const [timeLeft, setTimeLeft] = useState(120)
  const [timerActive, setTimerActive] = useState(true)
  const [expired, setExpired] = useState(false)
  const [verified, setVerified] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')

  const otpRefs = useRef([])
  const timerRef = useRef(null)
  const resendCountRef = useRef(0)

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!timerActive) return
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current)
          setTimerActive(false)
          setExpired(true)
          trackOtpTimeout({ time_elapsed: 120 })
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timerRef.current)
  }, [timerActive])

  function startTimer() {
    clearInterval(timerRef.current)
    setTimeLeft(120)
    setExpired(false)
    setTimerActive(true)
  }

  function formatTime(secs) {
    const m = String(Math.floor(secs / 60)).padStart(2, '0')
    const s = String(secs % 60).padStart(2, '0')
    return `${m}:${s}`
  }

  // ── OTP input handlers ─────────────────────────────────────────────────────
  function handleOtpChange(e, idx) {
    if (e.target.value.length > 1) {
      // Handle paste or autofill (iOS Safari fires onChange with full code)
      handleOtpPaste(null, e.target.value)
      return
    }
    const val = e.target.value.replace(/\D/g, '').slice(0, 1)
    const newOtp = [...otp]
    newOtp[idx] = val
    setOtp(newOtp)
    setOtpError('')
    if (val && idx < 5) otpRefs.current[idx + 1]?.focus()
  }

  function handleOtpKeyDown(e, idx) {
    if (e.key === 'Backspace' && !otp[idx] && idx > 0) {
      otpRefs.current[idx - 1]?.focus()
    }
  }

  // ── IMPROVED: cross-browser paste handler ──────────────
  // Handles: manual paste, iOS Safari autofill (onChange), Web OTP API
  function handleOtpPaste(e, value) {
    if (e && e.preventDefault) e.preventDefault()
    const raw =
      value ??
      (e?.clipboardData || window.clipboardData)?.getData('text') ??
      ''
    const pasted = raw.replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const newOtp = Array(6)
      .fill('')
      .map((_, i) => pasted[i] || '')
    setOtp(newOtp)
    setOtpError('')
    const next = Math.min(pasted.length - 1, 5)
    otpRefs.current[next]?.focus()
  }

  // ── Build the identity payload
  //    Always send both mobile + profileId.
  //    Backend resolves: profileId wins if valid, mobile is the fallback.
  function buildIdentityPayload() {
    return {
      mobile: mobile || undefined,
      profileId: getCookie('profileId') || undefined,
    }
  }

  // ── Resend ─────────────────────────────────────────────────────────────────
  async function handleResend() {
    try {
      const rateLimitStatus = consumeRateLimit(AUTH_RATE_LIMIT)

      if (rateLimitStatus.blocked) {
        setOtpError(AUTH_RATE_LIMIT.message)
        return
      }

      await api.post('/user/resend-otp', {
        ...buildIdentityPayload(),
      })

      setOtp(['', '', '', '', '', ''])
      setOtpError('')
      setSuccessMsg(t('auth.otpVerify'))
      resendCountRef.current += 1
      trackOtpResend({ attempt_count: resendCountRef.current })

      startTimer()

      setTimeout(() => otpRefs.current[0]?.focus(), 100)
    } catch (err) {
      const error = handleAxiosError(err)
      if (err?.response?.status === 429) {
        applyServerRateLimit(
          AUTH_RATE_LIMIT,
          err?.response?.headers?.['retry-after']
        )
      }
      setOtpError(error.message)
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  // Wrapped in useCallback so the Web OTP API useEffect always gets
  // the latest version without stale closure issues.
  // prefillOtp is passed by the Web OTP API useEffect; otherwise uses state.
  const handleSubmit = useCallback(
    async (e, prefillOtp) => {
      if (e && e.preventDefault) e.preventDefault()

      const otpVal = (prefillOtp ?? otp).join('')

      if (otpVal.length < 6) {
        setOtpError(t('auth.enterAllDigits'))
        return
      }

      try {
        const rateLimitStatus = consumeRateLimit(AUTH_RATE_LIMIT)

        if (rateLimitStatus.blocked) {
          setOtpError(AUTH_RATE_LIMIT.message)
          return
        }

        setVerifying(true)

        // Always send both — backend resolves which to use
        await api.post('/user/verify-otp', {
          ...buildIdentityPayload(),
          verification_code: otpVal,
        })

        clearInterval(timerRef.current)

        trackOtpEntered({ is_valid: true })
        const language = localStorage.getItem('appLanguage') || 'en'
        const state = localStorage.getItem('userState') || ''
        trackSignupComplete({
          state,
          language: getAnalyticsLanguageLabel(language),
          reg_flow_version: 'v1',
        })
        trackOtpConversion()

        setOtpError('')
        setVerified(true)

        localStorage.setItem('isLoggedIn', 'true')

        setTimeout(() => {
          window.location.href = '/batch-scan'
        }, 500)
      } catch (err) {
        if (err?.response?.status === 429) {
          applyServerRateLimit(
            AUTH_RATE_LIMIT,
            err?.response?.headers?.['retry-after']
          )
          setOtpError(err?.response?.data?.message || AUTH_RATE_LIMIT.message)
        } else {
          trackOtpEntered({ is_valid: false })
          setOtpError(t('auth.wrongOtp'))
        }
      } finally {
        setVerifying(false)
      }
    },
    [otp, mobile, t]
  )

  // Keep a stable ref so the Web OTP effect never needs handleSubmit as a dep
  const handleSubmitRef = useRef(handleSubmit);
  useEffect(() => { handleSubmitRef.current = handleSubmit; }, [handleSubmit]);

  // ── NEW: Web OTP API — Android Chrome SMS autofill ─────
  // Fires on mount (timer starts as true so OTP screen is immediately visible).
  // Chrome intercepts the incoming SMS and fills all 6 boxes automatically.
  // iOS Safari and desktop browsers ignore this silently.
  useEffect(() => {
    if (!('OTPCredential' in window)) return // not supported → skip

    const ac = new AbortController()

    navigator.credentials
      .get({ otp: { transport: ['sms'] }, signal: ac.signal })
      .then((credential) => {
        if (!credential?.code) return

        const digits = credential.code.replace(/\D/g, '').slice(0, 6)
        const newOtp = Array(6).fill('').map((_, i) => digits[i] || '')
        setOtp(newOtp)
        setOtpError('')

        // Auto-submit only when we got all 6 digits
        if (digits.length === 6) {
          handleSubmitRef.current(null, newOtp) // use ref to avoid stale closure
        }
      })
      .catch((err) => {
        console.log('Web OTP API failed or was dismissed', err);
        // User dismissed, timeout, or browser denied — no action needed
      })

    return () => ac.abort() // cleanup if component unmounts
  }, [])  // ← handleSubmit intentionally excluded: ref keeps it current without re-running

  return (
    <>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
      <link rel="shortcut icon" href="assets/images/favicon.ico" type="image/x-icon" />
      <link rel="stylesheet" href="assets/css/styles.css?ver=0.8" />
      <title>OTP Verification | Britannia</title>

      <div className="page-wrapper">
        <div className="main-wrapper yellow-bg">
          <div className="frame-outer">
            <span className="frame-corner tl" />
            <span className="frame-corner tr" />
            <span className="frame-corner bl" />
            <span className="frame-corner br" />

            <div className="frame-border-outer">
              <div className="frame-border">
                <div className="frame-border-inner">
                  <div className="frame-canvas">
                    <img
                      className="frame-bg-image"
                      src="assets/images/star-bg.webp"
                      alt="Star Element"
                    />

                    <div className="frame-content">
                      <div className="signup-screen">
                        <div className="signup-container">
                          {/* Header */}
                          <div className="signup-header">
                            <div className="signup-logo-wrap">
                              <img
                                src="assets/images/animate-britania-logo.webp"
                                alt="Britannia"
                                className="signup-logo signup-logo-1"
                              />
                              <img
                                src="assets/images/animate-milk-bikkis-logo.webp"
                                alt="Milk Bikis"
                                className="signup-logo signup-logo-2"
                              />
                            </div>
                            <img
                              src="assets/images/new-signup-logo.webp"
                              alt="திருக்குறள் சேலஞ்ச்"
                              className="signup-title-img"
                            />
                          </div>

                          {/* Form body */}
                          <div className="signup-body">
                            <h2 className="signup-heading">{t('auth.enterOtp')}</h2>

                            <form
                              className="signup-form"
                              id="otpForm"
                              onSubmit={handleSubmit}
                            >
                              {/* Subtitle */}
                              <p className="otp-sent-text">
                                {t('auth.otpSent')}{' '}
                                <span
                                  className="otp-phone-link"
                                  onClick={() => navigate('/register')}
                                >
                                  +91 {mobile}
                                </span>{' '}
                                <img
                                  src="assets/images/edit-icon.webp"
                                  alt="Edit"
                                  className="otp-edit-icon"
                                  onClick={() => navigate('/register')}
                                />
                              </p>

                              {/* OTP fields */}
                              <div className="otp-wrapper">
                                {otp.map((digit, idx) => (
                                  <input
                                    key={idx}
                                    ref={(el) => (otpRefs.current[idx] = el)}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    className="otp-input"
                                    autoComplete="one-time-code"
                                    value={digit}
                                    onChange={(e) => handleOtpChange(e, idx)}
                                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                    onPaste={handleOtpPaste}
                                    disabled={verified}
                                    style={otpError ? { borderColor: '#ff4444' } : {}}
                                  />
                                ))}
                              </div>

                              {/* Error + timer + resend row */}
                              <div className="otp-timer-row">
                                {otpError && (
                                  <span
                                    className="otp-error-text"
                                    style={{ display: 'inline' }}
                                  >
                                    {otpError}
                                  </span>
                                )}

                                {!expired && !verified && (
                                  <>
                                    <span className="otp-expire-text">
                                      {t('auth.codeExpire')}
                                    </span>
                                    <span className="otp-timer" id="otpTimer">
                                      {formatTime(timeLeft)}
                                    </span>
                                  </>
                                )}

                                {expired && !verified && (
                                  <a
                                    id="otpResendLink"
                                    className="otp-resend-link"
                                    style={{ display: 'inline', cursor: 'pointer' }}
                                    onClick={handleResend}
                                  >
                                    {t('auth.resendCode')}
                                  </a>
                                )}

                                {successMsg && (
                                  <span style={{ color: '#0c6', fontWeight: 500 }}>
                                    {successMsg}
                                  </span>
                                )}
                              </div>

                              {/* Submit button */}
                              <button
                                className="btn-next"
                                type="submit"
                                disabled={verified || verifying}
                              >
                                <span>
                                  {verifying
                                    ? t('common.verifying')
                                    : verified
                                    ? `✓ ${t('auth.done')}`
                                    : t('auth.go')}
                                </span>
                              </button>
                            </form>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Footer />
          </div>
        </div>
      </div>
    </>
  )
}

export default OTPVerification